/**
 * PostHog, kept off the critical path.
 *
 * Measured on the deployed song page: PostHog was the largest third-party cost
 * in a 1,323KB eager bundle -- one chunk of 182KB is pure SDK, and more of it
 * rides in the 228KB framework chunk. On top of that the SDK injects five more
 * scripts of its own (web-vitals, exception-autocapture, dead-clicks-
 * autocapture, config, surveys), which started fetching at 1,796ms and
 * competed with the page's own JavaScript for the network.
 *
 * The cause was a plain `import posthog from "posthog-js"` in
 * instrumentation-client.ts and nine components. A static import anywhere in
 * the eager graph pulls the whole SDK into the first load, so getting it out
 * means nothing may import it directly -- hence this module. It is the ONLY
 * place allowed to reference "posthog-js", and analytics-client.test.ts fails
 * if that stops being true.
 *
 * Nothing is lost by loading late. Calls made before the SDK arrives are
 * buffered and replayed in order, so early events -- the ones that land during
 * exactly the slow first seconds we are trying to fix -- still get through.
 */
import type { PostHog } from "posthog-js";

type Pending = (posthog: PostHog) => void;

let client: PostHog | null = null;
let loadStarted = false;

/**
 * Bounded so a misconfigured token (SDK never arrives) leaks a fixed amount
 * rather than growing for the life of the tab. Far above what a session
 * realistically fires before the SDK lands.
 */
const MAX_BUFFERED = 200;
const buffered: Pending[] = [];
let dropped = 0;

function run(fn: Pending): void {
  if (client) {
    fn(client);
    return;
  }
  if (buffered.length >= MAX_BUFFERED) {
    dropped++;
    return;
  }
  buffered.push(fn);
}

/** True once the SDK is present and initialised. Mirrors `posthog.__loaded`. */
export function analyticsReady(): boolean {
  return !!client?.__loaded;
}

export function capture(event: string, properties?: Record<string, unknown>): void {
  run((p) => p.capture(event, properties));
}

export function identify(id: string, properties?: Record<string, unknown>): void {
  run((p) => p.identify(id, properties));
}

export function register(properties: Record<string, unknown>): void {
  run((p) => p.register(properties));
}

export function unregister(property: string): void {
  run((p) => p.unregister(property));
}

export function setPersonProperties(properties: Record<string, unknown>): void {
  run((p) => p.people?.set?.(properties));
}

export function resetAnalytics(): void {
  run((p) => p.reset());
}

/**
 * The distinct id, or undefined before the SDK lands.
 *
 * Callers use this to avoid re-identifying an already-identified user. Before
 * load there is no id to compare against, so they should just call identify()
 * -- it is buffered, and PostHog's own identify is idempotent.
 */
export function distinctId(): string | undefined {
  return client?.__loaded ? client.get_distinct_id() : undefined;
}

/**
 * A feature flag's value, or undefined before the SDK lands.
 *
 * Undefined means "not known yet", never "off". Callers already had to handle
 * this -- flags resolve asynchronously even with the SDK loaded eagerly -- so
 * they fall back to their default variant and correct themselves in
 * onFeatureFlags below.
 */
export function featureFlag(key: string): string | boolean | undefined {
  return client?.__loaded ? client.getFeatureFlag(key) : undefined;
}

/** Subscribe to flag resolution. Returns an unsubscribe that is safe to call
 *  whether or not the SDK had arrived by the time it runs. */
export function onFeatureFlags(callback: () => void): () => void {
  let unsubscribe: (() => void) | undefined;
  let cancelled = false;

  run((p) => {
    if (cancelled) return;
    const result = p.onFeatureFlags(() => callback());
    if (typeof result === "function") unsubscribe = result;
  });

  return () => {
    cancelled = true;
    unsubscribe?.();
  };
}

/**
 * Fetch and initialise the SDK, then flush anything buffered.
 *
 * Safe to call more than once; only the first call does work.
 */
export async function startAnalytics(
  token: string,
  options: Parameters<PostHog["init"]>[1],
): Promise<void> {
  if (loadStarted) return;
  loadStarted = true;

  let posthog: PostHog;
  try {
    posthog = (await import("posthog-js")).default;
  } catch (err) {
    // Analytics must never take the page down with it. Buffered calls are
    // dropped here deliberately -- there is nothing to replay them into.
    console.warn("[analytics] PostHog failed to load", err);
    buffered.length = 0;
    return;
  }

  posthog.init(token, options);
  client = posthog;

  if (dropped > 0) {
    console.warn(`[analytics] dropped ${dropped} events before PostHog loaded`);
  }
  const pending = buffered.splice(0, buffered.length);
  for (const fn of pending) fn(posthog);
}
